import { SUBSCRIPTION_TIERS } from './subscription.js';

export const DIGITAL_PRODUCTS = {
  RESILIENCE_READINESS_FRAMEWORK: {
    id: 'resilience_readiness_framework',
    name: 'Resilience Readiness Framework',
    description: 'A framework for assessing organizational resilience',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  CRISIS_PLAN: {
    id: 'crisis_plan',
    name: 'Crisis Plan',
    description: 'A comprehensive crisis management plan template',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  BUSINESS_CONTINUITY_PLAN: {
    id: 'business_continuity_plan',
    name: 'Business Continuity Plan',
    description: 'A template for creating a business continuity plan',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  CRISIS_COMMUNICATION_PLAN: {
    id: 'crisis_communication_plan',
    name: 'Crisis Communication Plan',
    description: 'A template for crisis communication planning',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  VENDOR_RISK_MANAGEMENT_PLAN: {
    id: 'vendor_risk_management_plan',
    name: 'Vendor Risk Management Plan',
    description: 'A template for vendor risk management',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  POST_INCIDENT_REVIEW_TEMPLATE: {
    id: 'post_incident_review_template',
    name: 'Post Incident Review',
    description: 'A template for conducting post-incident reviews',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  HIGH_RISK_TRAVEL_PLAN: {
    id: 'high_risk_travel_plan',
    name: 'High Risk Travel Plan',
    description: 'A template for high-risk travel planning',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  MEDICAL_EMERGENCY: {
    id: 'medical_emergency',
    name: 'Medical Emergency',
    description: 'A template for medical emergency response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  CYBER_INCIDENT: {
    id: 'cyber_incident',
    name: 'Cyber',
    description: 'A template for cyber incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  CRITICAL_INCIDENT: {
    id: 'critical_incident',
    name: 'Critical Incident',
    description: 'A template for critical incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  SUSPICIOUS_ITEM_BOMB_THREAT: {
    id: 'suspicious_item_bomb_threat',
    name: 'Suspicious Item/Bomb Threat',
    description: 'A template for suspicious item and bomb threat response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  ACTIVE_THREAT: {
    id: 'active_threat',
    name: 'Active Threat',
    description: 'A template for active threat response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  FIRE: {
    id: 'fire',
    name: 'Fire',
    description: 'A template for fire incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  FLOOD: {
    id: 'flood',
    name: 'Flood',
    description: 'A template for flood incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  EARTHQUAKE: {
    id: 'earthquake',
    name: 'Earthquake',
    description: 'A template for earthquake incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  MEETING_EVENT_INCIDENT: {
    id: 'meeting_event_incident',
    name: 'Meeting/Event Incident',
    description: 'A template for meeting and event incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  CYCLONE: {
    id: 'cyclone',
    name: 'Cyclone',
    description: 'A template for cyclone incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  KIDNAP_KRE: {
    id: 'kidnap_kre',
    name: 'Kidnap for ransom and extortion (KRE)',
    description: 'A template for kidnap and extortion incident response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  ADVERSE_EMPLOYEE_SEPARATION: {
    id: 'adverse_employee_separation',
    name: 'Adverse Employee Separation',
    description: 'A template for adverse employee separation response',
    subscriptionTiers: [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  EVACUATION_PREPAREDNESS: {
    id: 'evacuation_preparedness',
    name: 'Evacuation Checklist',
    description: 'A template for evacuation preparedness',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  },
  PANDEMIC_READINESS: {
    id: 'pandemic_readiness',
    name: 'Pandemic Preparedness Checklist',
    description: 'A template for pandemic readiness',
    subscriptionTiers: [SUBSCRIPTION_TIERS.BUSINESS_PRO, SUBSCRIPTION_TIERS.ENTERPRISE_PRO]
  }
};

export function getLatestVersion(product) {
  return product.versions[product.versions.length - 1];
}

export function getVersionHistory(product) {
  return product.versions.map(version => ({
    version: version.version,
    createdAt: version.createdAt,
    createdBy: version.createdBy,
    changes: version.changes
  }));
}

export function canAccessProduct(product, subscriptionTier) {
  // If org has ENTERPRISE_PRO, they can access everything
  if (subscriptionTier === SUBSCRIPTION_TIERS.ENTERPRISE_PRO) {
    return true;
  }
  
  // If org has BUSINESS_PRO, they can only access BUSINESS_PRO products
  if (subscriptionTier === SUBSCRIPTION_TIERS.BUSINESS_PRO) {
    return product.subscriptionTiers.includes(SUBSCRIPTION_TIERS.BUSINESS_PRO);
  }
  
  return false;
}

export function getAvailableProducts(subscriptionTier) {
  return Object.values(DIGITAL_PRODUCTS).filter(product => 
    canAccessProduct(product, subscriptionTier)
  );
}

export function trackProductChange(productId, version, changeType, userId, details) {
  return {
    type: changeType,
    userId,
    timestamp: new Date(),
    details,
    metadata: {
      productId,
      version
    }
  };
}

export const PRODUCT_CHANGE_TYPES = {
  CREATED: 'created',
  UPDATED: 'updated',
  VERSION_ADDED: 'version_added',
  VERSION_ARCHIVED: 'version_archived',
  ACCESS_CHANGED: 'access_changed',
  DELETED: 'deleted'
}; 